import type { OrderLine, Tare, WeighPlate } from '../types'

/**
 * Satır kimliği ve tutarı, tek yerde.
 *
 * Sıradan satırda tutar adet × birim fiyat. Tartılı satırda değil: tutar
 * tabakların toplamı, bekleyen tartımda 0. Kimlik de tartımın id'sini
 * içeriyor; aynı börek iki ayrı tartımla iki ayrı satır.
 */

/** İki satır aynı mı (birleştirme, çıkarma, "yeni ne eklendi"). */
export function sameLine(a: OrderLine, b: OrderLine): boolean {
  return (
    a.productId === b.productId &&
    (a.note ?? '') === (b.note ?? '') &&
    a.unitPriceKurus === b.unitPriceKurus &&
    (a.byName ?? '') === (b.byName ?? '') &&
    (a.weigh?.id ?? '') === (b.weigh?.id ?? '') &&
    (a.portion ?? '') === (b.portion ?? '')
  )
}

export const isPendingWeigh = (l: Pick<OrderLine, 'weigh'>) => l.weigh?.status === 'pending'
export const isWeighed = (l: Pick<OrderLine, 'weigh'>) => l.weigh?.status === 'done'

/**
 * Satırı başka bir adetle: tutarı ona göre.
 *
 * Tartılmış satırda adet tabak sayısı; bir kısmı alınırsa (parçalı ödeme)
 * baştan ya da sondan o kadar tabak, kendi tutarlarıyla. `from: 'end'`
 * kalan kısmı vermek için.
 */
export function withQty(line: OrderLine, qty: number, from: 'start' | 'end' = 'start'): OrderLine {
  const w = line.weigh
  if (w?.status === 'pending') return { ...line, qty, lineTotalKurus: 0 }
  if (w?.status === 'done' && w.plates) {
    const plates = from === 'start' ? w.plates.slice(0, qty) : w.plates.slice(w.plates.length - qty)
    return {
      ...line,
      qty,
      lineTotalKurus: plates.reduce((s, p) => s + p.kurus, 0),
      weigh: { ...w, plates },
    }
  }
  return { ...line, qty, lineTotalKurus: line.unitPriceKurus * qty }
}

/** Bir tabağın net gramı ve tutarı. Kilo fiyatı kuruş, sonuç tam kuruş. */
export function plateOf(grossGr: number, tareGr: number, perKgKurus: number): WeighPlate {
  const netGr = Math.max(0, Math.round(grossGr - tareGr))
  return { grossGr, netGr, kurus: Math.round((netGr * perKgKurus) / 1000) }
}

/** Tartımı satıra işler: tutar tabakların toplamı, adet tabak sayısı. */
export function applyWeigh(
  line: OrderLine,
  tare: Tare | undefined,
  plates: WeighPlate[],
  weighedBy?: string,
): OrderLine {
  const w = line.weigh!
  return {
    ...line,
    qty: plates.length,
    lineTotalKurus: plates.reduce((s, p) => s + p.kurus, 0),
    weigh: { ...w, status: 'done', tare, plates, ...(weighedBy ? { weighedBy } : {}) },
  }
}

export const totalNetGr = (plates: WeighPlate[] = []) => plates.reduce((s, p) => s + p.netGr, 0)

/** "1,180 kg" ya da "350 g". */
export function formatGr(gr: number): string {
  return gr >= 1000
    ? `${(gr / 1000).toLocaleString('tr-TR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} kg`
    : `${gr} g`
}

export const newWeighId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID().slice(0, 8)
    : Math.random().toString(36).slice(2, 10)
